import { useIsAuthenticated, useMsal, useAccount } from "@azure/msal-react";
import {
  AccountInfo,
  AuthenticationResult,
  InteractionRequiredAuthError,
  InteractionStatus,
} from "@azure/msal-browser";
import { useState, useEffect, useCallback } from "react";

import {
  interactiveLoginRequest,
  loginRequest,
  logoutRequest,
  silentRequest,
} from "us.helper/azure/adV2";
import {
  interactiveLoginRequestV1,
  silentRequestV1,
} from "us.helper/azure/adV1";
import { isPrivateMode } from "us.helper/utility";

export type AuthContextProps = {
  isAuthenticated: boolean;
  account: AccountInfo | null;
  loading: boolean;
  error: string | null;
  isInitialized: boolean;
  authResult: AuthenticationResult | null;
  login: () => Promise<AuthenticationResult | null | undefined>;
  logout: () => Promise<void>;
  getV1Token: (forceRefresh?: boolean) => Promise<AuthenticationResult | null>;
};

export const useAuth = (): AuthContextProps => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount(accounts[0] || null);

  const [authResult, setAuthResult] = useState<AuthenticationResult | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const clearSession = () => {
    setAuthResult(null);
    setError(null);
    instance.setActiveAccount(null);
    // safer clearing: only MSAL keys
    Object.keys(localStorage).forEach((key) => {
      if (key.includes("msal.") || key.includes("azure")) {
        localStorage.removeItem(key);
      }
    });
    Object.keys(sessionStorage).forEach((key) => {
      if (key.includes("msal.") || key.includes("azure")) {
        sessionStorage.removeItem(key);
      }
    });
  };

  const login = useCallback(async () => {
    if (inProgress !== InteractionStatus.None) return null;
    setLoading(true);
    setError(null);

    try {
      // 1. Try acquireTokenSilent if we already have an account
      if (accounts.length > 0) {
        try {
          const silentResult = await instance.acquireTokenSilent({
            ...silentRequest,
            account: accounts[0],
          });
          setAuthResult(silentResult);
          return silentResult;
        } catch (silentError) {
          console.warn("Silent token failed, trying SSO silent", silentError);
        }
      }

      // 2. Final fallback → redirect login (safe for cross-subdomain SSO)
      await instance.loginRedirect(interactiveLoginRequest);
      return null; // redirect takes over
    } catch (err) {
      console.error("Login flow failed", err);
      setError(err instanceof Error ? err.message : "Login failed");
      throw err;
    } finally {
      setLoading(false);
    }
  }, [accounts, inProgress, instance]);

  const logout = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const allAccounts = instance.getAllAccounts();
      if (allAccounts.length > 0) {
        await instance.logoutRedirect({
          ...logoutRequest,
          account: allAccounts[0],
          postLogoutRedirectUri: window.location.origin,
          idTokenHint: authResult?.idToken,
        });
        return;
      }
      clearSession();
    } catch (logoutError) {
      console.error("Logout error:", logoutError);
      clearSession();
      setError("Logout completed with warnings");
    } finally {
      setLoading(false);
    }
  }, [instance, authResult]);

  const getV1Token = useCallback(
    async (forceRefresh = false) => {
      if (!account || inProgress !== InteractionStatus.None) return null;

      try {
        return await instance.acquireTokenSilent({
          ...silentRequestV1,
          account,
          forceRefresh,
        });
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          try {
            return await instance.acquireTokenPopup({
              ...interactiveLoginRequestV1,
              account,
            });
          } catch (popupError) {
            console.error("V1 popup acquisition failed", popupError);
            return null;
          }
        }
        console.error("V1 token failed", error);
        return null;
      }
    },
    [account, inProgress, instance]
  );

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      try {
        // Private browsing? Skip silent auth
        const privateMode = await isPrivateMode();
        if (privateMode) {
          console.warn("Private browsing detected — skipping silent SSO");
          setIsInitialized(true);
          setLoading(false);
          return;
        }

        // Handle redirect if user was redirected back
        const redirectResponse = await instance.handleRedirectPromise();
        if (redirectResponse) {
          setAuthResult(redirectResponse);
          instance.setActiveAccount(redirectResponse.account);
        }

        const currentAccounts = instance.getAllAccounts();
        if (currentAccounts.length > 0) {
          try {
            const silentResult = await instance.acquireTokenSilent({
              ...silentRequest,
              account: currentAccounts[0],
            });
            setAuthResult(silentResult);
          } catch (silentError) {
            console.warn("Init: Silent token failed", silentError);
            try {
              const ssoResult = await instance.ssoSilent(loginRequest);
              setAuthResult(ssoResult);
            } catch (ssoError) {
              console.warn("Init: SSO silent failed", ssoError);
              clearSession();
            }
          }
        } else {
          try {
            const ssoResult = await instance.ssoSilent(loginRequest);
            setAuthResult(ssoResult);
          } catch (ssoError) {
            console.warn("Init: No accounts + SSO silent failed", ssoError);
            clearSession();
          }
        }
      } catch (initError) {
        console.error("MSAL init error:", initError);
        setError("Authentication initialization failed");
      } finally {
        setIsInitialized(true);
        setLoading(false);
      }
    };

    initialize();
  }, [instance]);

  return {
    isAuthenticated,
    isInitialized,
    account,
    authResult,
    loading,
    error,
    login,
    logout,
    getV1Token,
  };
};
